import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, Send, Bot, User, Trash2, Sparkles, Upload, FileText, X } from "lucide-react";
import { cn } from "@/lib/utils";
import DashboardTopbar from "@/components/layout/DashboardTopbar";
import { useToast } from "@/hooks/use-toast";
import { Markdown } from "@/components/Markdown";
import { geminiProxy } from "@/lib/backendAI";

// Selectable models — confirmed available against the live Gemini API catalog.
const MODELS = [
  { id: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
  { id: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
  { id: "gemini-3.1-flash-lite", label: "Gemini 3.1 Flash Lite" },
  { id: "gemini-3.5-flash", label: "Gemini 3.5 Flash" },
  { id: "gemini-3.5-flash-lite", label: "Gemini 3.5 Flash Lite" },
  { id: "gemini-3.6-flash", label: "Gemini 3.6 Flash" },
] as const;
const DEFAULT_MODEL = "gemini-2.5-flash";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

const SUGGESTED_QUESTIONS = [
  "Summarise the key financial highlights from this document",
  "What are the main audit qualifications or emphasis of matter paragraphs?",
  "What are the related party transactions disclosed?",
  "Explain the EPS calculation shown in this document",
  "What revenue recognition policies are followed?",
  "Are there any contingent liabilities disclosed?",
];

// Convert File to base64
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip data URI prefix (data:application/pdf;base64,)
      resolve(result.split(",")[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Call Gemini with optional PDF + chat history
async function callGemini(
  userMessage: string,
  pdfBase64: string | null,
  history: ChatMessage[],
  model: string = DEFAULT_MODEL
): Promise<string> {
  // Build contents array from history + new message
  const contents: any[] = [];

  // Add conversation history (last 10 turns to stay within token limits)
  const recent = history.slice(-10);
  for (const msg of recent) {
    contents.push({
      role: msg.role === "user" ? "user" : "model",
      parts: [{ text: msg.content }],
    });
  }

  // Build current user turn parts
  const userParts: any[] = [];

  // Attach PDF on first message only (it's part of context throughout)
  if (pdfBase64 && history.length === 0) {
    userParts.push({
      inlineData: {
        mimeType: "application/pdf",
        data: pdfBase64,
      },
    });
  }

  userParts.push({ text: userMessage });
  contents.push({ role: "user", parts: userParts });

  const systemInstruction = pdfBase64
    ? `You are a highly knowledgeable financial audit and compliance assistant. 
A PDF document has been uploaded. Answer questions about this document accurately and concisely.
When referencing figures or sections, cite the relevant part of the document.
Focus on audit compliance, Ind AS standards, SEBI regulations, and financial reporting requirements.`
    : `You are a highly knowledgeable financial audit and compliance assistant specialising in:
- Indian Accounting Standards (Ind AS)
- SEBI LODR regulations
- Companies Act 2013
- Audit standards (SA series)
- Financial statement analysis and compliance
Provide clear, precise, and professional answers.`;

  const body = {
    system_instruction: { parts: [{ text: systemInstruction }] },
    contents,
    generationConfig: {
      temperature: 0.3,
      maxOutputTokens: 4096,
    },
  };

  const data = await geminiProxy(model, body);
  return data?.candidates?.[0]?.content?.parts?.[0]?.text || "No response received.";
}

const Chatbot: React.FC = () => {
  const { toast } = useToast();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [model, setModel] = useState<string>(DEFAULT_MODEL);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const [isExtractingPdf, setIsExtractingPdf] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast({ title: "Invalid file", description: "Please upload a PDF file.", variant: "destructive" });
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast({ title: "File too large", description: "Maximum 100 MB.", variant: "destructive" });
      return;
    }

    setIsExtractingPdf(true);
    try {
      const b64 = await fileToBase64(file);
      setPdfFile(file);
      setPdfBase64(b64);
      setMessages([]);
      toast({ title: "PDF ready", description: `${file.name} loaded. Start asking questions.` });
    } catch {
      toast({ title: "Error", description: "Failed to process PDF.", variant: "destructive" });
    } finally {
      setIsExtractingPdf(false);
    }
  };

  const removePdf = () => {
    setPdfFile(null);
    setPdfBase64(null);
    setMessages([]);
  };

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isTyping) return;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: trimmed,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsTyping(true);

    try {
      // For subsequent messages, rebuild context by attaching PDF inline in the system message
      // We pass pdfBase64 only on first turn; for continuation we include it in system context
      const b64ForCall = messages.length === 0 ? pdfBase64 : null;

      // If we have a PDF and it's not the first message, we need to re-send the PDF as context
      // Gemini stateless API: include PDF on every call when document context is needed
      const effectiveBase64 = pdfBase64; // always attach when available

      const reply = await callGemini(trimmed, effectiveBase64, messages, model);
      const botMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: reply,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, botMsg]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      const errMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: `Sorry, I encountered an error: ${msg}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errMsg]);
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => setMessages([]);

  const hasPdf = !!pdfFile;

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardTopbar />
      <div className="pt-20 px-4 sm:px-6 lg:px-8 pb-6">
        <div className="max-w-4xl mx-auto space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <MessageCircle className="h-8 w-8 text-blue-600" />
                Compliance Chatbot
              </h1>
              <p className="text-gray-600 mt-1">
                Upload a PDF and ask questions, or chat about audit &amp; compliance topics
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={model}
                onChange={e => setModel(e.target.value)}
                disabled={isTyping}
                title="Model used for the next message"
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
              >
                {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
              {messages.length > 0 && (
                <Button variant="outline" size="sm" onClick={clearChat} className="flex items-center gap-2">
                  <Trash2 className="h-4 w-4" />
                  Clear Chat
                </Button>
              )}
            </div>
          </div>

          {/* PDF Upload bar */}
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={handleFileUpload}
              disabled={isExtractingPdf || isTyping}
            />
            {pdfFile ? (
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2 flex-1">
                <FileText className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-sm text-green-800 font-medium truncate flex-1">{pdfFile.name}</span>
                <span className="text-xs text-green-600 flex-shrink-0">{(pdfFile.size / 1024 / 1024).toFixed(1)} MB</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700 flex-shrink-0"
                  onClick={removePdf}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => fileInputRef.current?.click()}
                disabled={isExtractingPdf || isTyping}
              >
                {isExtractingPdf ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                    Processing PDF...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Upload PDF (optional)
                  </>
                )}
              </Button>
            )}
            <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50 flex-shrink-0">
              <Sparkles className="h-3 w-3 mr-1" />
              Gemini 2.5 Flash
            </Badge>
          </div>

          {/* Chat card */}
          <Card className="flex flex-col" style={{ height: "calc(100vh - 280px)" }}>
            <CardHeader className="border-b pb-3 flex-shrink-0 py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Bot className="h-5 w-5 text-blue-600" />
                AI Compliance Assistant
                {hasPdf && (
                  <Badge variant="secondary" className="ml-2 text-xs">
                    Document context active
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>

            <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center px-4">
                    <Bot className="h-14 w-14 text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 mb-2">How can I help you today?</h3>
                    <p className="text-sm text-gray-500 mb-6 max-w-md">
                      {hasPdf
                        ? `PDF loaded: "${pdfFile!.name}". Ask anything about this document.`
                        : "Upload a PDF to analyse a document, or ask general compliance and audit questions."}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg">
                      {SUGGESTED_QUESTIONS.map((q, i) => (
                        <button
                          key={i}
                          onClick={() => { setInput(q); textareaRef.current?.focus(); }}
                          className="text-left text-xs p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-gray-600 hover:text-blue-700"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  messages.map(msg => (
                    <div
                      key={msg.id}
                      className={cn("flex gap-3", msg.role === "user" ? "justify-end" : "justify-start")}
                    >
                      {msg.role === "assistant" && (
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <Bot className="h-4 w-4 text-blue-600" />
                        </div>
                      )}
                      <div
                        className={cn(
                          "max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed",
                          msg.role === "user"
                            ? "bg-blue-600 text-white rounded-br-md"
                            : "bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm"
                        )}
                      >
                        {msg.role === "assistant"
                          ? <Markdown content={msg.content} />
                          : <div className="whitespace-pre-wrap">{msg.content}</div>}
                        <div className={cn("text-[10px] mt-1.5", msg.role === "user" ? "text-blue-200" : "text-gray-400")}>
                          {msg.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                      {msg.role === "user" && (
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="h-4 w-4 text-gray-600" />
                        </div>
                      )}
                    </div>
                  ))
                )}

                {isTyping && (
                  <div className="flex gap-3 justify-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                      <div className="flex space-x-1.5 items-center h-4">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Input */}
              <div className="border-t p-4 flex-shrink-0 bg-white">
                <div className="flex gap-2 items-end">
                  <Textarea
                    ref={textareaRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={hasPdf ? "Ask about the uploaded document..." : "Ask a compliance or audit question..."}
                    className="resize-none min-h-[44px] max-h-[120px] bg-gray-50"
                    rows={1}
                    disabled={isTyping || isExtractingPdf}
                  />
                  <Button
                    onClick={handleSend}
                    disabled={!input.trim() || isTyping || isExtractingPdf}
                    size="icon"
                    className="h-11 w-11 flex-shrink-0"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2">Enter to send · Shift+Enter for new line</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
